import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { 
  Umbrella, MapPin, Calendar, Users, Wifi, Car, 
  Utensils, Tv, CheckCircle, ArrowRight, Phone, Star, 
  Sun, Palmtree, Camera, Heart, Loader2
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = '';

const destinations = [
  {
    name: 'Goa',
    image: 'https://images.unsplash.com/photo-1512343879784-a960bf40e7f2?auto=format&fit=crop&w=600',
    properties: 45,
    startingPrice: '3,500'
  },
  {
    name: 'Ooty',
    image: 'https://images.unsplash.com/photo-1582510003544-4d00b7f74220?auto=format&fit=crop&w=600',
    properties: 28,
    startingPrice: '2,800'
  },
  {
    name: 'Manali',
    image: 'https://images.unsplash.com/photo-1626621341517-bbf3d9990a23?auto=format&fit=crop&w=600',
    properties: 32,
    startingPrice: '3,200'
  },
  {
    name: 'Pondicherry',
    image: 'https://images.unsplash.com/photo-1582510003544-4d00b7f74220?auto=format&fit=crop&w=600',
    properties: 22,
    startingPrice: '2,500'
  },
];

const amenities = [
  { icon: Wifi, label: 'Free WiFi' },
  { icon: Car, label: 'Free Parking' },
  { icon: Utensils, label: 'Kitchen' },
  { icon: Tv, label: 'Smart TV' },
  { icon: Sun, label: 'Balcony/Garden' },
  { icon: Camera, label: 'Scenic Views' },
];

const benefits = [
  'Fully furnished vacation homes',
  'Flexible check-in/check-out',
  'Local experiences & recommendations',
  '24/7 guest support',
  'Professional cleaning before arrival',
  'No hidden fees - transparent pricing',
];

const VacationRentals = () => {
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    destination: '',
    check_in: '',
    check_out: '',
    guests: '2',
    property_type: 'villa',
    budget: '',
    special_requests: ''
  });

  const isValidEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  const isValidPhone = (phone) => phone.replace(/\D/g, '').length >= 10;

  const validateForm = () => {
    const newErrors = {};
    if (!formData.name.trim()) newErrors.name = 'Name is required';
    if (!formData.email.trim()) newErrors.email = 'Email is required';
    else if (!isValidEmail(formData.email)) newErrors.email = 'Please enter a valid email';
    if (!formData.phone.trim()) newErrors.phone = 'Phone is required';
    else if (!isValidPhone(formData.phone)) newErrors.phone = 'Please enter a valid phone number';
    if (!formData.destination) newErrors.destination = 'Please select a destination';
    if (!formData.check_in) newErrors.check_in = 'Check-in date is required';
    if (!formData.check_out) newErrors.check_out = 'Check-out date is required';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) {
      toast.error('Please fill in all required fields correctly');
      return;
    }
    setSubmitting(true);
    try {
      const response = await fetch(`${API_URL}/api/leads/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          business_line: 'vacation_rentals',
          name: formData.name,
          email: formData.email,
          phone: formData.phone,
          city: formData.destination,
          message: formData.special_requests,
          source: 'website',
          metadata: {
            destination: formData.destination,
            check_in: formData.check_in,
            check_out: formData.check_out,
            guests: formData.guests,
            property_type: formData.property_type,
            budget: formData.budget
          }
        })
      });
      if (response.ok) {
        setSubmitted(true);
        toast.success('Booking request submitted! Our team will contact you within 24-48 hours.');
      } else {
        toast.error('Something went wrong. Please try again.');
      }
    } catch (err) {
      toast.error('Network error. Please try again later.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleTalkToUs = () => {
    window.open('https://wa.me/918790460259?text=Hi%20ZenStudios!%20I%27m%20interested%20in%20vacation%20rental%20properties.', '_blank');
  };

  return (
    <div className="min-h-screen bg-white pt-20" data-testid="vacation-rentals-page">
      {/* Hero */}
      <section className="relative py-20 bg-gradient-to-br from-amber-50 via-orange-50 to-primary/10 overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1507525428034-b723cf961d3e?auto=format&fit=crop&w=2000')] bg-cover bg-center" />
        </div>
        <div className="relative max-w-7xl mx-auto px-6">
          <div className="max-w-2xl">
            <div className="inline-flex items-center gap-2 bg-amber-100 text-amber-700 px-4 py-2 rounded-full text-sm font-medium mb-6">
              <Umbrella className="w-4 h-4" /> Vacation Rentals
            </div>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6">
              Your Perfect <span className="text-primary">Getaway</span> Awaits
            </h1>
            <p className="text-lg text-gray-600 mb-8">
              Discover handpicked vacation homes for unforgettable holidays. From beach villas to mountain retreats, find your home away from home.
            </p>
            <div className="flex gap-4 flex-wrap">
              <Button 
                size="lg"
                className="rounded-full bg-primary text-white px-8"
                onClick={() => document.getElementById('booking-form')?.scrollIntoView({ behavior: 'smooth' })}
              >
                Book Now <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
              <Button 
                size="lg"
                variant="outline"
                className="rounded-full px-8"
                onClick={handleTalkToUs}
                data-testid="vacation-talk-to-us-btn"
              >
                <Phone className="w-5 h-5 mr-2" /> Talk to Us
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Popular Destinations */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">Popular Destinations</h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Explore our curated collection of vacation properties in India's most loved destinations.
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {destinations.map((dest, idx) => (
              <div 
                key={idx} 
                className="group relative rounded-2xl overflow-hidden shadow-lg cursor-pointer"
                onClick={() => setFormData({...formData, destination: dest.name})}
              >
                <img loading="lazy" decoding="async" 
                  src={dest.image} 
                  alt={dest.name}
                  className="w-full h-64 object-cover group-hover:scale-110 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent" />
                <div className="absolute bottom-0 left-0 right-0 p-6 text-white">
                  <h3 className="text-xl font-bold mb-1">{dest.name}</h3>
                  <p className="text-sm text-white/80">{dest.properties} properties</p>
                  <p className="text-sm font-semibold mt-2">From ₹{dest.startingPrice}/night</p>
                </div>
                <button className="absolute top-4 right-4 w-10 h-10 bg-white/20 backdrop-blur rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                  <Heart className="w-5 h-5 text-white" />
                </button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Amenities */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-2xl font-bold mb-2">Every Stay Includes</h2>
            <p className="text-gray-600">Premium amenities for a comfortable vacation</p>
          </div>
          <div className="flex flex-wrap justify-center gap-6">
            {amenities.map((item, idx) => (
              <div key={idx} className="flex items-center gap-3 bg-white px-6 py-4 rounded-full shadow-sm">
                <item.icon className="w-5 h-5 text-primary" />
                <span className="font-medium">{item.label}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits + Form */}
      <section className="py-20" id="booking-form">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-16">
            {/* Benefits */}
            <div>
              <div className="inline-flex items-center gap-2 bg-primary/10 text-primary px-4 py-2 rounded-full text-sm font-medium mb-6">
                <Palmtree className="w-4 h-4" /> Why ZenStudios Vacations
              </div>
              <h2 className="text-3xl md:text-4xl font-bold mb-6">Experience Hassle-Free Holidays</h2>
              <p className="text-gray-600 mb-8">
                We understand that vacations are precious. That's why we've handpicked properties that deliver exceptional experiences, every single time.
              </p>
              <div className="space-y-4">
                {benefits.map((benefit, idx) => (
                  <div key={idx} className="flex items-center gap-3 bg-amber-50 p-4 rounded-xl">
                    <CheckCircle className="w-5 h-5 text-primary flex-shrink-0" />
                    <span className="font-medium">{benefit}</span>
                  </div>
                ))}
              </div>
              
              <div className="mt-8 p-6 bg-gradient-to-br from-primary/10 to-amber-50 rounded-xl border border-primary/20">
                <div className="flex items-center gap-2 mb-2">
                  <Star className="w-5 h-5 text-amber-500 fill-amber-500" />
                  <Star className="w-5 h-5 text-amber-500 fill-amber-500" />
                  <Star className="w-5 h-5 text-amber-500 fill-amber-500" />
                  <Star className="w-5 h-5 text-amber-500 fill-amber-500" />
                  <Star className="w-5 h-5 text-amber-500 fill-amber-500" />
                </div>
                <p className="text-gray-700 italic mb-2">"Amazing experience! The villa was exactly as shown. ZenStudios made our Goa trip unforgettable."</p>
                <p className="text-sm text-gray-500">— Priya S., Mumbai</p>
              </div>
            </div>

            {/* Form */}
            <div className="bg-gray-50 p-8 rounded-2xl">
              <h3 className="text-2xl font-bold mb-2">Book Your Vacation Home</h3>
              <p className="text-gray-600 mb-6">Fill in your details and we'll find the perfect stay for you.</p>
              <form onSubmit={handleSubmit} className="space-y-5">
                <div>
                  <Label>Full Name *</Label>
                  <Input
                    value={formData.name}
                    onChange={(e) => {
                      setFormData({...formData, name: e.target.value});
                      if (errors.name) setErrors({...errors, name: ''});
                    }}
                    placeholder="Your name"
                    className={`rounded-xl mt-1 ${errors.name ? 'border-red-500' : ''}`}
                  />
                  {errors.name && <p className="text-red-500 text-xs mt-1">{errors.name}</p>}
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Email *</Label>
                    <Input
                      type="email"
                      value={formData.email}
                      onChange={(e) => {
                        setFormData({...formData, email: e.target.value});
                        if (errors.email) setErrors({...errors, email: ''});
                      }}
                      placeholder="you@email.com"
                      className={`rounded-xl mt-1 ${errors.email ? 'border-red-500' : ''}`}
                    />
                    {errors.email && <p className="text-red-500 text-xs mt-1">{errors.email}</p>}
                  </div>
                  <div>
                    <Label>Phone *</Label>
                    <Input
                      type="tel"
                      value={formData.phone}
                      onChange={(e) => {
                        setFormData({...formData, phone: e.target.value});
                        if (errors.phone) setErrors({...errors, phone: ''});
                      }}
                      placeholder="+91 98765 43210"
                      className={`rounded-xl mt-1 ${errors.phone ? 'border-red-500' : ''}`}
                    />
                    {errors.phone && <p className="text-red-500 text-xs mt-1">{errors.phone}</p>}
                  </div>
                </div>
                <div>
                  <Label>Destination *</Label>
                  <Select 
                    value={formData.destination} 
                    onValueChange={(v) => {
                      setFormData({...formData, destination: v});
                      if (errors.destination) setErrors({...errors, destination: ''});
                    }}
                  >
                    <SelectTrigger className={`rounded-xl mt-1 ${errors.destination ? 'border-red-500' : ''}`}>
                      <SelectValue placeholder="Select destination" />
                    </SelectTrigger>
                    <SelectContent>
                      {destinations.map(d => (
                        <SelectItem key={d.name} value={d.name}>{d.name}</SelectItem>
                      ))}
                      <SelectItem value="other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                  {errors.destination && <p className="text-red-500 text-xs mt-1">{errors.destination}</p>}
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Check-in Date *</Label>
                    <Input
                      type="date"
                      value={formData.check_in}
                      onChange={(e) => {
                        setFormData({...formData, check_in: e.target.value});
                        if (errors.check_in) setErrors({...errors, check_in: ''});
                      }}
                      className={`rounded-xl mt-1 ${errors.check_in ? 'border-red-500' : ''}`}
                    />
                    {errors.check_in && <p className="text-red-500 text-xs mt-1">{errors.check_in}</p>}
                  </div>
                  <div>
                    <Label>Check-out Date *</Label>
                    <Input
                      type="date"
                      value={formData.check_out}
                      onChange={(e) => {
                        setFormData({...formData, check_out: e.target.value});
                        if (errors.check_out) setErrors({...errors, check_out: ''});
                      }}
                      className={`rounded-xl mt-1 ${errors.check_out ? 'border-red-500' : ''}`}
                    />
                    {errors.check_out && <p className="text-red-500 text-xs mt-1">{errors.check_out}</p>}
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Number of Guests</Label>
                    <Select value={formData.guests} onValueChange={(v) => setFormData({...formData, guests: v})}>
                      <SelectTrigger className="rounded-xl mt-1">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {['1', '2', '3', '4', '5', '6', '7', '8+'].map(n => (
                          <SelectItem key={n} value={n}>{n} {n === '1' ? 'Guest' : 'Guests'}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Property Type</Label>
                    <Select value={formData.property_type} onValueChange={(v) => setFormData({...formData, property_type: v})}>
                      <SelectTrigger className="rounded-xl mt-1">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="villa">Villa</SelectItem>
                        <SelectItem value="apartment">Apartment</SelectItem>
                        <SelectItem value="cottage">Cottage</SelectItem>
                        <SelectItem value="farmhouse">Farmhouse</SelectItem>
                        <SelectItem value="any">Any</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div>
                  <Label>Special Requests (Optional)</Label>
                  <Textarea
                    value={formData.special_requests}
                    onChange={(e) => setFormData({...formData, special_requests: e.target.value})}
                    placeholder="Any special requirements or preferences..."
                    className="rounded-xl mt-1"
                    rows={3}
                  />
                </div>
                <Button type="submit" className="w-full rounded-full bg-primary py-6" data-testid="vacation-submit-btn">
                  Request Booking <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
                <p className="text-center text-sm text-gray-500">
                  Or call us directly at <a href="tel:+918790460259" className="text-primary font-medium">+91 87904 60259</a>
                </p>
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-gradient-to-br from-amber-500 to-orange-500">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-3xl font-bold text-white mb-4">Need Help Planning Your Trip?</h2>
          <p className="text-white/90 mb-8">
            Our travel experts are here to help you find the perfect vacation rental.
          </p>
          <Button 
            size="lg"
            className="rounded-full bg-white text-amber-600 hover:bg-gray-100 px-8"
            onClick={handleTalkToUs}
            data-testid="vacation-cta-talk-btn"
          >
            <Phone className="w-5 h-5 mr-2" /> Talk to Us
          </Button>
        </div>
      </section>
    </div>
  );
};

export default VacationRentals;
